/**
 * useTracking Hook
 * Tracks scroll depth, time on page, and section views
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { trackingAPI } from '../utils/api';

/**
 * useTracking hook - manages all tracking analytics
 * @param {string} prospectId - The prospect being tracked
 * @param {string} sessionId - Session identifier
 * @returns {Object} - { recordSectionView, recordSectionScroll }
 */
export function useTracking(prospectId, sessionId) {
  const pageStartTimeRef = useRef(Date.now());
  const sectionTimersRef = useRef({});
  const scrollTimeoutRef = useRef(null);
  const viewedSectionsRef = useRef(new Set());

  // Track time when prospect leaves page
  useEffect(() => {
    const pageStartTime = pageStartTimeRef.current;
    const sectionTimers = sectionTimersRef.current;

    return () => {
      if (!prospectId || !sessionId) return;

      const totalSeconds = Math.floor((Date.now() - pageStartTime) / 1000);
      const sectionTimes = {};

      // Calculate section times
      Object.entries(sectionTimers).forEach(([sectionId, startTime]) => {
        if (startTime) {
          sectionTimes[sectionId] = Math.floor((Date.now() - startTime) / 1000);
        }
      });

      // Send final tracking data
      trackingAPI
        .recordTimeOnPage(prospectId, sessionId, totalSeconds, sectionTimes)
        .catch((err) => console.warn('Failed to record time on page:', err));
    };
  }, [prospectId, sessionId]);

  // Record that a section came into view
  const recordSectionView = useCallback(
    (sectionId) => {
      if (!prospectId || !sessionId || !sectionId) return;

      // Only record each section once per session
      if (viewedSectionsRef.current.has(sectionId)) return;
      viewedSectionsRef.current.add(sectionId);

      // Start timer for this section
      if (!sectionTimersRef.current[sectionId]) {
        sectionTimersRef.current[sectionId] = Date.now();
      }

      // Send tracking event
      trackingAPI.recordSectionView(prospectId, sessionId, sectionId).catch((err) => {
        console.warn(`Failed to record section view for ${sectionId}:`, err);
      });
    },
    [prospectId, sessionId]
  );

  // Record scroll depth for a section
  const recordSectionScroll = useCallback(
    (sectionId, scrollPercentage) => {
      if (!prospectId || !sessionId || !sectionId) return;

      // Debounce scroll events (max 1 per 2 seconds per section)
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = setTimeout(() => {
        trackingAPI
          .recordSectionScroll(prospectId, sessionId, sectionId, scrollPercentage)
          .catch((err) => {
            console.warn(`Failed to record scroll for ${sectionId}:`, err);
          });
      }, 2000);
    },
    [prospectId, sessionId]
  );

  return {
    recordSectionView,
    recordSectionScroll,
  };
}

/**
 * useIntersectionObserver hook - detect when elements become visible
 * Useful for triggering section view events
 * @param {React.RefObject} ref - React ref to observe
 * @param {Function} callback - Called when element becomes visible
 * @param {Object} options - IntersectionObserver options
 */
export function useIntersectionObserver(ref, callback, options = {}) {
  const { threshold = 0.1, rootMargin = '0px' } = options;

  useEffect(() => {
    if (!ref?.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && callback) {
          callback(entry);
        }
      },
      { threshold, rootMargin }
    );

    const currentRef = ref.current;
    observer.observe(currentRef);

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [ref, callback, threshold, rootMargin]);
}

/**
 * useScrollDepth hook - calculate scroll percentage within an element
 * @param {React.RefObject} ref - React ref to the scrollable element
 * @param {Function} onScroll - Called with scroll percentage (0-100)
 */
export function useScrollDepth(ref, onScroll) {
  useEffect(() => {
    if (!ref?.current) return;

    const handleScroll = () => {
      const element = ref.current;
      if (!element) return;

      // Calculate scroll percentage
      const scrollHeight = element.scrollHeight - element.clientHeight;
      const scrollPercentage =
        scrollHeight > 0 ? Math.round((element.scrollTop / scrollHeight) * 100) : 0;

      if (onScroll) {
        onScroll(scrollPercentage);
      }
    };

    const element = ref.current;
    element.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      element.removeEventListener('scroll', handleScroll);
    };
  }, [ref, onScroll]);
}

/**
 * usePageVisibility hook - detect when user switches tabs
 * Useful for pausing activities when page is hidden
 * @returns {boolean} - true if page is visible
 */
export function usePageVisibility() {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsVisible(document.visibilityState === 'visible');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  return isVisible;
}

export default useTracking;
